import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Filter, Target, TrendingUp, TrendingDown, ChevronDown } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';
import { useFilterStore } from '@/features/filters';
import { buildStatsQuery } from '@/features/filters/utils';
import { BottomSheet, PageHeader } from '@/shared/ui';
import { useStats } from '@/features/stats/hooks/useStats';
import { useBrandsControllerGetBrands } from '@/generated/api/brands/brands';
import { haptic } from '@/shared/utils/telegram';
import { formatInteger, formatCurrency } from '@/shared/utils/formatting';
import type { TimePreset } from '@/shared/types';

const PERIOD_OPTIONS = [
  { value: 'today' as const, label: '1D', fullLabel: 'Today' },
  { value: 'yesterday' as const, label: 'YD', fullLabel: 'Yesterday' },
  { value: 'this_week' as const, label: '7D', fullLabel: 'This Week' },
  { value: 'this_month' as const, label: 'M', fullLabel: 'This Month' },
  { value: 'this_quarter' as const, label: 'Q', fullLabel: 'This Quarter' },
  { value: 'this_year' as const, label: 'YTD', fullLabel: 'This Year' },
];

export function StatsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const period = useFilterStore((s) => s.period);
  const setPeriod = useFilterStore((s) => s.setPeriod);
  const brands = useFilterStore((s) => s.brands);
  const setBrands = useFilterStore((s) => s.setBrands);
  const fromURLSearchParams = useFilterStore((s) => s.fromURLSearchParams);
  const toURLSearchParams = useFilterStore((s) => s.toURLSearchParams);

  // Fetch brands list to auto-select first brand
  const { data: brandsData } = useBrandsControllerGetBrands();

  // Set default period to 'today' on first mount
  useEffect(() => {
    if (searchParams.toString()) {
      fromURLSearchParams(searchParams);
    } else if (period !== 'today') {
      setPeriod('today');
    }
  }, []);

  // No auto-selection - allow "All Brands" view by default
  // User can manually select a brand from the dropdown

  useEffect(() => {
    const params = toURLSearchParams();
    navigate({ search: params.toString() }, { replace: true });
  }, [period, brands, navigate, toURLSearchParams]);

  // Build query - allow All Brands aggregation when no brand selected
  const filters = useFilterStore();
  const query = buildStatsQuery(filters, undefined, true); // allowAllBrands = true

  // Stats query will be disabled if query is null
  const { data, isLoading, error } = useStats(query);

  const handlePeriodChange = (newPeriod: TimePreset) => {
    haptic.selection();
    setPeriod(newPeriod);
  };

  const handleBrandChange = (brand: string) => {
    haptic.impact('light');
    if (brand === '__all__') {
      setBrands([]); // Empty array means all brands
    } else {
      setBrands([brand]);
    }
  };

  // Use pre-calculated conversion rates from backend
  const clickToReg = data?.summary?.click2reg ?? 0;
  const regToFtd = data?.summary?.reg2ftd ?? 0;
  const regToCpa = data?.summary?.reg2cpa ?? 0;

  // Determine chart title and time format based on period
  const isHourly = period === 'today' || period === 'yesterday';
  const chartTimeLabel = isHourly ? 'Hourly' : 'Daily';
  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isHourly) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Show loading while brands are being fetched
  const isLoadingBrands = !brandsData && brands.length === 0;
  const selectedBrand = brands.length > 0 ? brands[0] : '__all__';

  return (
    <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-top-4 duration-500 pb-20">
      {/* Header & Quick Period - Sticky */}
      <div className="flex flex-col gap-3 sticky top-0 bg-[var(--tg-theme-bg-color)] z-20 pt-2 pb-1">
        <PageHeader title="Overview" />

        {/* Brand Selector */}
        {brandsData?.data?.brands && brandsData.data.brands.length > 0 && (
          <div className="relative">
            <select
              value={selectedBrand}
              onChange={(e) => handleBrandChange(e.target.value)}
              className="w-full h-11 px-4 pr-10 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl text-[13px] font-bold appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
            >
              <option value="__all__">🌐 All Brands</option>
              {brandsData.data.brands.map((brand) => (
                <option key={brand.brand} value={brand.brand}>
                  {brand.brand} ({brand.totalEvents} events)
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
              <ChevronDown size={16} className="text-[var(--tg-theme-hint-color)]" />
            </div>
          </div>
        )}

        {/* Period Selector */}
        <div className="flex p-1 bg-[var(--tg-theme-secondary-bg-color)] rounded-xl overflow-x-auto scrollbar-hide">
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.value}
              onClick={() => handlePeriodChange(p.value)}
              title={p.fullLabel}
              className={`flex-1 py-2.5 text-sm font-black rounded-lg transition-all min-w-[44px] ${
            period === p.value
              ? 'bg-[var(--tg-theme-bg-color)] shadow-sm text-[var(--tg-theme-link-color)]'
              : 'text-[var(--tg-theme-hint-color)] opacity-60'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-5 animate-in fade-in slide-in-from-right duration-300">
        {/* Loading */}
        {(isLoading || isLoadingBrands) && (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--tg-theme-link-color)] border-t-transparent" />
              <p className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase">Loading stats...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="p-6 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-center">
            <p className="text-sm font-bold text-rose-600">Failed to load statistics</p>
            <p className="text-xs text-rose-500/80 mt-1">Please try again later</p>
          </div>
        )}

        {/* Stats KPI Cards */}
        {data && (
          <>
            {/* Main KPI Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Clicks Card */}
              <div className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] flex flex-col gap-1 border border-black/5 dark:border-white/5">
                <span className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">Clicks</span>
                <span className="text-2xl font-black tabular-nums">{formatInteger(data.summary.totalClicks ?? 0)}</span>
              </div>

              {/* REG Card - secondary KPI */}
              <div className="p-4 rounded-2xl bg-blue-500/10 flex flex-col gap-1 border border-blue-500/20">
                <span className="text-[10px] font-black text-blue-600 uppercase tracking-wider">REG</span>
                <span className="text-2xl font-black text-blue-600 tabular-nums">{formatInteger(data.summary.totalRegs ?? 0)}</span>
              </div>

              {/* CPA Card - PRIMARY KPI (v2.0.0) */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 flex flex-col gap-1.5 border-2 border-amber-500/30">
                <span className="text-[10px] font-black text-amber-600 uppercase tracking-wider">CPA</span>
                <span className="text-3xl font-black text-amber-600 tabular-nums">{formatInteger(data.summary.totalCpas ?? 0)}</span>
              </div>

              {/* FTD Card - tracking metric */}
              <div className="p-4 rounded-2xl bg-emerald-500/10 flex flex-col gap-1 border border-emerald-500/20">
                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-wider">FTD</span>
                <span className="text-2xl font-black text-emerald-600 tabular-nums">{formatInteger(data.summary.totalFtds ?? 0)}</span>
              </div>
            </div>

            {/* Secondary Metrics */}
            <div className="grid grid-cols-2 gap-3">
              {/* REDEP Card */}
              <div className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] flex flex-col gap-1 border border-black/5 dark:border-white/5">
                <span className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">REDEP</span>
                <span className="text-2xl font-black tabular-nums">{formatInteger(data.summary.totalRedeps ?? 0)}</span>
              </div>

              {/* DEP Card */}
              <div className="p-4 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] flex flex-col gap-1 border border-black/5 dark:border-white/5">
                <span className="text-[10px] font-black text-[var(--tg-theme-hint-color)] uppercase tracking-wider">DEP</span>
                <span className="text-2xl font-black tabular-nums">{formatInteger(data.summary.totalDeps ?? 0)}</span>
              </div>
            </div>

            {/* Conversion Funnel */}
            <div className="bg-[var(--tg-theme-secondary-bg-color)] p-4 rounded-3xl flex flex-col gap-3 border border-black/5 dark:border-white/5">
              <h3 className="text-xs font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest">Conversion Funnel</h3>

              <div className="space-y-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-[var(--tg-theme-hint-color)] uppercase">Clicks → REG</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-2xl font-black tabular-nums ${clickToReg > 10 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {clickToReg.toFixed(2)}%
                      </span>
                      {clickToReg > 10 ? (
                        <TrendingUp size={16} className="text-emerald-500" />
                      ) : (
                        <TrendingDown size={16} className="text-rose-500" />
                      )}
                    </div>
                  </div>
                  <div className="h-2 w-full bg-black/5 dark:bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${clickToReg > 10 ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ width: `${Math.min(clickToReg, 100)}%` }} />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-[var(--tg-theme-hint-color)] uppercase">REG → FTD</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-2xl font-black tabular-nums ${regToFtd > 15 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {regToFtd.toFixed(2)}%
                      </span>
                      {regToFtd > 15 ? (
                        <TrendingUp size={16} className="text-emerald-500" />
                      ) : (
                        <TrendingDown size={16} className="text-rose-500" />
                      )}
                    </div>
                  </div>
                  <div className="h-3 w-full bg-black/5 dark:bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${regToFtd > 15 ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ width: `${Math.min(regToFtd, 100)}%` }} />
                  </div>
                </div>

                {/* REG → CPA - PRIMARY CONVERSION METRIC (v2.0.0) */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-[var(--tg-theme-hint-color)] uppercase">REG → CPA</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-2xl font-black tabular-nums ${regToCpa > 20 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {regToCpa.toFixed(2)}%
                      </span>
                      {regToCpa > 20 ? (
                        <TrendingUp size={16} className="text-emerald-500" />
                      ) : (
                        <TrendingDown size={16} className="text-rose-500" />
                      )}
                    </div>
                  </div>
                  <div className="h-3 w-full bg-black/5 dark:bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${regToCpa > 20 ? 'bg-emerald-500' : 'bg-rose-500'}`} style={{ width: `${Math.min(regToCpa, 100)}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* CPA Revenue Card (v2.0.0: totalAmount now represents CPA earnings only) */}
            {data.summary.totalAmount !== undefined && parseFloat(data.summary.totalAmount) > 0 && (
              <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-transparent p-4 rounded-3xl border border-amber-500/20">
                <div className="text-xs font-black text-amber-600 uppercase tracking-widest mb-1.5">CPA Revenue</div>
                <div className="text-3xl font-black text-amber-600 tabular-nums">{formatCurrency(parseFloat(data.summary.totalAmount), data.summary.currency)}</div>
              </div>
            )}

            {/* FTDs Chart - Only show if there are FTDs */}
            {data.timeSeries && data.timeSeries.length > 0 && (data.summary.totalFtds ?? 0) > 0 && (
              <div className="bg-[var(--tg-theme-secondary-bg-color)] p-4 rounded-3xl border border-black/5 dark:border-white/5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest">{chartTimeLabel} FTDs</h3>
                  <Target size={16} className="text-[var(--tg-theme-link-color)]" />
                </div>
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.timeSeries.map((point) => ({
                      time: formatTime(point.period),
                      value: point.ftds || 0,
                    }))}>
                      <defs>
                        <linearGradient id="colorFtds" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#10b981"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorFtds)"
                      />
                      <XAxis dataKey="time" hide />
                      <YAxis hide />
                      <Tooltip
                        contentStyle={{
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor: 'rgba(0,0,0,0.8)',
                          color: '#fff',
                          fontSize: '10px',
                          fontWeight: 'bold',
                        }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* CPAs Chart - Only show if there are CPAs */}
            {data.timeSeries && data.timeSeries.length > 0 && (data.summary.totalCpas ?? 0) > 0 && (
              <div className="bg-[var(--tg-theme-secondary-bg-color)] p-4 rounded-3xl border border-black/5 dark:border-white/5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-black text-[var(--tg-theme-hint-color)] uppercase tracking-widest">{chartTimeLabel} CPAs</h3>
                  <Target size={16} className="text-amber-600" />
                </div>
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.timeSeries.map((point) => ({
                      time: formatTime(point.period),
                      value: point.cpas || 0,
                    }))}>
                      <defs>
                        <linearGradient id="colorCpas" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#d97706" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#d97706" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#d97706"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorCpas)"
                      />
                      <XAxis dataKey="time" hide />
                      <YAxis hide />
                      <Tooltip
                        contentStyle={{
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor: 'rgba(0,0,0,0.8)',
                          color: '#fff',
                          fontSize: '10px',
                          fontWeight: 'bold',
                        }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Filter Bottom Sheet - Placeholder */}
      <BottomSheet isOpen={filterOpen} onClose={() => setFilterOpen(false)} title="Advanced Filters">
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <div className="w-16 h-16 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center">
            <Filter size={32} className="text-[var(--tg-theme-hint-color)]" />
          </div>
          <div className="text-center">
            <h3 className="text-sm font-bold text-[var(--tg-theme-text-color)] mb-2">Advanced Filters</h3>
            <p className="text-xs text-[var(--tg-theme-hint-color)]">
              Additional filtering options coming soon
            </p>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}
